import { deleteApp, initializeApp } from "firebase/app";
import {
  inMemoryPersistence,
  initializeAuth,
  linkWithCredential,
  signInWithCustomToken,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { app, auth, functions } from "../firebase";
import { requestNativeAppleCredential } from "./appleAuth";

export const APPLE_LINK_CONFIRMATION = "LINK_EXISTING_VAULTQUEST_ACCOUNT";

let linkingInFlight = null;

async function authenticateInIsolatedSession(identifier, password) {
  const secondaryApp = initializeApp(
    app.options,
    `vaultquest-apple-link-${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
  const secondaryAuth = initializeAuth(secondaryApp, {
    persistence: inMemoryPersistence,
  });

  try {
    const cleanIdentifier = String(identifier || "").trim();
    let credential;

    if (cleanIdentifier.includes("@")) {
      credential = await signInWithEmailAndPassword(
        secondaryAuth,
        cleanIdentifier.toLowerCase(),
        password
      );
    } else {
      const signInWithUsernameSecure = httpsCallable(
        functions,
        "signInWithUsernameSecure"
      );
      const response = await signInWithUsernameSecure({
        username: cleanIdentifier.toLowerCase(),
        password,
        apiKey: app.options.apiKey,
      });
      const customToken = String(response?.data?.customToken || "");
      if (!customToken) throw new Error("USERNAME_LOGIN_FAILED");
      credential = await signInWithCustomToken(secondaryAuth, customToken);
    }

    return {
      uid: credential.user.uid,
      idToken: await credential.user.getIdToken(true),
      user: credential.user,
      cleanup: () => deleteApp(secondaryApp).catch(() => undefined),
    };
  } catch (error) {
    await deleteApp(secondaryApp).catch(() => undefined);
    throw error;
  }
}

export function linkExistingPasswordAccountToApple({
  identifier,
  password,
  primaryAuth = auth,
  callableFunctions = functions,
  requestAppleCredential = requestNativeAppleCredential,
  authenticateExistingAccount = authenticateInIsolatedSession,
} = {}) {
  if (linkingInFlight) return linkingInFlight;

  linkingInFlight = (async () => {
    const temporaryAppleUid = String(primaryAuth.currentUser?.uid || "");
    if (!temporaryAppleUid) throw new Error("APPLE_LINK_SESSION_MISSING");

    const target = await authenticateExistingAccount(identifier, password);
    try {
      if (
        !target?.idToken ||
        !target?.uid ||
        !target?.user ||
        target.uid === temporaryAppleUid
      ) {
        throw new Error("APPLE_LINK_TARGET_INVALID");
      }

      // Obtain a fresh Apple credential before the temporary Auth user is removed.
      // If Apple is cancelled, no account state has changed yet.
      const { credential: appleCredential } = await requestAppleCredential();

      const finalizeTemporaryAppleAccountLinkSecure = httpsCallable(
        callableFunctions,
        "finalizeTemporaryAppleAccountLinkSecure"
      );
      const prepared = await finalizeTemporaryAppleAccountLinkSecure({
        targetIdToken: target.idToken,
        confirmation: APPLE_LINK_CONFIRMATION,
      });
      const targetCustomToken = String(prepared?.data?.customToken || "");
      if (!targetCustomToken) throw new Error("APPLE_LINK_TARGET_TOKEN_MISSING");

      // Link while the password-authenticated target remains isolated. Only after
      // linking succeeds does the primary app session switch to the preserved UID.
      try {
        const linkedCredential = await linkWithCredential(
          target.user,
          appleCredential
        );
        await signInWithCustomToken(primaryAuth, targetCustomToken);
        return linkedCredential.user;
      } catch (linkError) {
        // The temporary account is already gone at this point. Keep the verified
        // existing VaultQuest account usable even if Apple linking itself fails.
        await signInWithCustomToken(primaryAuth, targetCustomToken);
        throw linkError;
      }
    } finally {
      await target?.cleanup?.();
    }
  })().finally(() => {
    linkingInFlight = null;
  });

  return linkingInFlight;
}
